import { EditorState } from "../types/editor-state.types";
import {
  createPositionsFromTree,
  getLocationForNewNode,
} from "./position.helpers";
import { EditorAction } from "../types/editor-action.types";
import { Tree } from "src/types/tree.types";
import {
  createTreeFromPositions,
  fixInactiveTreeNodes,
} from "src/features/tree";
import produce from "immer";

export function editorStateReducer(
  state: EditorState,
  action: EditorAction
): EditorState {
  switch (action.type) {
    case "UPDATE_NODE": {
      return produce(state, (draft) => {
        draft.nodes[action.node.id] = action.node;
      });
    }

    case "DELETE_ALL": {
      return {
        ...state,
        focusedNodeId: undefined,
        nodes: {},
        positions: {},
      };
    }

    case "CLEAN_UP_POSITIONS": {
      return produce(state, (draft) => {
        const tree = createTreeFromPositions(draft.positions, draft.nodes);
        const fixedTree = fixInactiveTreeNodes(tree, draft.nodes);
        draft.canvasView.x = -50;
        draft.canvasView.y = -100;
        draft.positions = createPositionsFromTree(fixedTree);
      });
    }

    case "UPDATE_POSITION": {
      return produce(state, (draft) => {
        draft.positions[action.id] = {
          nodeId: action.id,
          x: action.x,
          y: action.y,
        };
      });
    }

    case "UPDATE_CANVAS_VIEW": {
      return {
        ...state,
        canvasView: {
          ...state.canvasView,
          ...action.view,
        },
      };
    }

    case "ADD_NODE": {
      return produce(state, (draft) => {
        const { x, y } = getLocationForNewNode(draft.positions, draft.nodes);
        draft.nodes[action.node.id] = action.node;
        draft.positions[action.node.id] = { nodeId: action.node.id, x, y };
        draft.focusedNodeId = action.node.id;
      });
    }

    case "DELETE_NODE": {
      return produce(state, (draft) => {
        const tree = createTreeFromPositions(draft.positions, draft.nodes);
        draft.focusedNodeId = getPrevFocusableNodeId(tree, action.nodeId);
        delete draft.nodes[action.nodeId];
        delete draft.positions[action.nodeId];
      });
    }

    case "FOCUS_NODE": {
      if (state.focusedNodeId === action.nodeId) return state;
      return produce(state, (draft) => {
        draft.focusedNodeId = action.nodeId;
      });
    }

    case "BLUR_NODE": {
      if (!state.focusedNodeId) return state;
      return produce(state, (draft) => {
        delete draft.focusedNodeId;
      });
    }

    case "FOCUS_NEXT_NODE": {
      return produce(state, (draft) => {
        const tree = createTreeFromPositions(draft.positions, draft.nodes);
        const index = tree.nodes.findIndex(
          (treeNode) => treeNode.node.id === action.nodeId
        );

        if (index === -1) return state;
        const maxIndex = tree.nodes.length - 1;
        const nextIndex = index === maxIndex ? 0 : index + 1;
        draft.focusedNodeId = tree.nodes[nextIndex].node.id;
      });
    }

    case "FOCUS_PREV_NODE": {
      return produce(state, (draft) => {
        const tree = createTreeFromPositions(draft.positions, draft.nodes);
        const index = tree.nodes.findIndex(
          (treeNode) => treeNode.node.id === action.nodeId
        );

        if (index === -1) return state;
        const minIndex = 0;
        const prevIndex =
          index === minIndex ? tree.nodes.length - 1 : index - 1;
        draft.focusedNodeId = tree.nodes[prevIndex].node.id;
      });
    }
  }
}

export function getPrevFocusableNodeId(
  tree: Tree,
  focusedNodeId?: string
): string | undefined {
  const focusedIndex = tree.nodes.findIndex(
    (treeNode) => treeNode.node.id === focusedNodeId
  );
  const prevIndex = focusedIndex <= 0 ? focusedIndex + 1 : focusedIndex - 1;
  const newFocusedNode = tree.nodes[prevIndex];
  return newFocusedNode?.node.id;
}
